import { Item } from 'fc-ui-components';
import * as _ from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { getArticleMainImageUrl, getArticlePrice, getFormattedColor } from 'helpers';
import { ApiModel } from 'models';
import { MinimumOrderRestriction, OrderRestrictionType } from 'services';
import { ColorGroupIdMap, GetByBrandAndStyleInputParams, ProductForOrder } from './searcher.types';

import { OrderArticleColor, PrepackForOrder } from 'app/b2b-order/types/order';

import { formatDeliveryDateToItem } from 'app/b2b-order/helpers/format-delivery-date';
import { formatPrice, getFormattedPrice } from 'app/b2b-order/helpers/price';
import { sortSizes } from 'app/b2b-order/helpers/size';
import { DeliveryDateRange, PreorderArticle, PreorderArticleSize } from 'app/b2b-order/types/preorder';
import { PrepackForReorder, ReorderArticle, ReorderArticleSize, StoreStockData } from 'app/b2b-order/types/reorder';
import { FcProductName } from 'types';

export type OrderProductName = Extract<FcProductName, FcProductName.OrderPlatform | FcProductName.PreorderPlatform>;
export type RelatedArticle = {
    mainProductStyleGroupId: string;
    mainProductColorGroupId: string;
    brandId: string;
    relatedProducts: [{ styleGroupId: string; colorGroupId: string }];
};

export class Searcher {
    constructor(private apiModel: ApiModel) {}

    public getArticleByBrandAndStyleForReorder({
        brandId,
        styleGroupId,
        language,
        retailerId
    }: GetByBrandAndStyleInputParams): Observable<{
        articles: ReorderArticle[];
        colors: OrderArticleColor[];
        hasExclusivityAccess: boolean;
        hasStoreStock: boolean;
    }> {
        const url = `${this.apiModel.getReorderEndpoint()}/products/brand/${brandId}/style/${encodeURIComponent(
            styleGroupId
        )}?language=${language}&retailerId=${retailerId}`;
        return this.apiModel.makeHttpObservableRequest({ url, method: 'GET' }).pipe(
            switchMap(({ products, prepacks }: { products: ProductForOrder[]; prepacks: PrepackForReorder[] }) => {
                const gtins = products.map((article) => article.gtin).flat();
                return this.getGtinsStoreStockDataForReorder(brandId, retailerId, gtins).pipe(
                    map((stockData: StoreStockData) => {
                        const productsWithMergedStocks = products.map((article) => ({
                            ...article,
                            stock: {
                                ...article.stock,
                                available: article.stock?.available || 0,
                                storeStock: stockData.stocks[article.gtin]
                            }
                        }));
                        return {
                            products: productsWithMergedStocks,
                            prepacks,
                            hasStoreStock: stockData.haveStocksInStores
                        };
                    })
                );
            }),
            map(
                ({
                    products,
                    prepacks,
                    hasStoreStock
                }: {
                    products: ProductForOrder[];
                    prepacks: PrepackForReorder[];
                    hasStoreStock: boolean;
                }) => {
                    const productsGrouppedByColor = _.groupBy(products, 'colorGroupId');
                    const prepacksGrouppedByColor = _.groupBy(prepacks, 'colorGroupId');

                    const colors = this.getColorsFromProducts(products);
                    const articles = _.map(colors, (color: OrderArticleColor) =>
                        this.mapProductsToReorderArticle({
                            products: productsGrouppedByColor[color.colorGroupId],
                            color,
                            brandId,
                            styleGroupId,
                            language,
                            prepacks: prepacksGrouppedByColor[color.colorGroupId] || []
                        })
                    );

                    const colorWithHasStock = _.map(colors, (color: OrderArticleColor) => {
                        const articleWithColor = _.find(
                            articles,
                            (article) => article.color.colorGroupId === color.colorGroupId
                        );
                        return {
                            ...color,
                            hasStock: articleWithColor.hasStock
                        };
                    });
                    return {
                        articles: this.sortArticlesByStock(articles),
                        colors: this.sortColorsByHasStock(colorWithHasStock),
                        hasExclusivityAccess: true,
                        hasStoreStock
                    };
                }
            ),
            // TODO: This is a hack to handle the error thrown by ApiModel.makeHttpObservableRequest
            catchError((error) => {
                // If error is a function (because of how ApiModel throws), call it to get the real error
                let realError = error;
                if (typeof error === 'function') {
                    try {
                        realError = error();
                    } catch (e) {
                        realError = error;
                    }
                }

                const status = realError?.status;

                if (status === 403) {
                    return of({ articles: [], colors: [], hasExclusivityAccess: false, hasStoreStock: false });
                } else {
                    return of({ articles: [], colors: [], hasExclusivityAccess: true, hasStoreStock: false });
                }
            })
        );
    }

    public getGtinsStoreStockDataForReorder(
        brandId: string,
        retailerId: string,
        gtins: string[]
    ): Observable<StoreStockData> {
        const gtinLimit = 300;
        const gtinsChunks: string[][] = _.chunk(gtins, gtinLimit);

        const urlInventoryData = `${this.apiModel.getReorderEndpoint()}/products/brand/${brandId}/inventory/stock`;
        return combineLatest(
            gtinsChunks.map((gtins) =>
                this.apiModel.makeHttpObservableRequest({
                    url: urlInventoryData,
                    method: 'GET',
                    params: { retailerId, gtins }
                })
            )
        ).pipe(
            map((responses) => {
                // depending on the gtin chunk, some might not have stock in stores
                // but if at least one has stock in stores, we can consider haveStocksInStores=true
                const haveStocksInStores = _.some(responses, (r) => r.haveStocksInStores);
                return _.merge({}, ...responses, { haveStocksInStores });
            }),
            catchError((error) => {
                console.error(error);
                return of({ stocks: {}, haveStocksInStores: false });
            })
        );
    }

    public getArticleByBrandAndStyleForPreorder({
        brandId,
        styleGroupId,
        language,
        retailerId
    }: GetByBrandAndStyleInputParams): Observable<{
        articles: PreorderArticle[];
        colors: OrderArticleColor[];
        hasExclusivityAccess: boolean;
    }> {
        const url = `${this.apiModel.getPreorderEndpoint()}/products/brand/${brandId}/style/${encodeURIComponent(
            styleGroupId
        )}?language=${language}&retailerId=${retailerId}`;
        return this.apiModel.makeHttpObservableRequest({ url, method: 'GET' }).pipe(
            map(({ orderableColors, products }: { orderableColors: ColorGroupIdMap; products: ProductForOrder[] }) => {
                const productsGrouppedByColor = _.groupBy(products, 'colorGroupId');

                const colors = this.getColorsFromProducts(products);
                const articles = _.map(colors, (color: OrderArticleColor) =>
                    this.mapProductsToPreorderArticle({
                        products: productsGrouppedByColor[color.colorGroupId],
                        color,
                        brandId,
                        styleGroupId,
                        language,
                        colorMap: orderableColors[color.colorGroupId]
                    })
                );

                return { articles, colors, hasExclusivityAccess: true };
            }),
            catchError((error) => {
                console.error(error);
                if (error.status === 403) {
                    return of({
                        articles: [],
                        colors: [],
                        hasExclusivityAccess: false
                    });
                } else {
                    return of({ articles: [], colors: [], hasExclusivityAccess: true });
                }
            })
        );
    }

    public getRelatedArticleByBrand(
        brandId: string,
        retailerId: string,
        styleGroupId: string,
        colorGroupId: string,
        product: OrderProductName = FcProductName.OrderPlatform
    ): Observable<ReorderArticle[]> {
        const url = `${this.apiModel.getReorderEndpoint()}/products/brand/${brandId}/style/${encodeURIComponent(
            styleGroupId
        )}/color/${encodeURIComponent(colorGroupId)}/related?retailerId=${retailerId}&product=${product}`;
        return this.apiModel.makeHttpObservableRequest({ url, method: 'GET' }).pipe(
            catchError((error) => {
                console.error(error);
                return of([]);
            })
        );
    }
    private mapProductsToReorderArticle({
        products,
        color,
        brandId,
        styleGroupId,
        language,
        prepacks
    }: {
        products: ProductForOrder[];
        color: OrderArticleColor;
        brandId: string;
        styleGroupId: string;
        language: string;
        prepacks: PrepackForReorder[];
    }): ReorderArticle {
        const sizes: ReorderArticleSize[] = _.map(products, (product: ProductForOrder) =>
            this.mapProductToReorderArticleSize(product, language)
        );
        const extractCertificates = _.find(sizes, 'manufacturerAttributes.certificates');
        const articlePrice = getArticlePrice(products);
        const firstSize = _.first(sizes);
        const article = {
            brandId,
            brandName: firstSize.brandName,
            styleGroupId,
            articleNumber: firstSize.articleNumber,
            name: firstSize.name,
            images: firstSize.images,
            imageUrl: getArticleMainImageUrl(
                _.map(products, (product) => ({ ...product, media: { images: product.images } }))
            ),
            isPreliminary: _.some(firstSize.images, { isPreliminary: true }),
            minimumOrderRestriction: this.getArticleMinimumOrderRestriction(products, language),
            stock: {
                available: this.getArticleAvailableStock(products),
                validSelectedAmount: true
            },
            hasDiscountedPurchasePrice: !!articlePrice.discountedPurchasePrice,
            showsDefaultPricelist: firstSize.showsDefaultPricelist,
            manufacturerAttributes: firstSize.manufacturerAttributes,
            fcAttributes: firstSize.fcAttributes,
            color,
            formattedColor: getFormattedColor(color),
            sizes: sortSizes(sizes) as ReorderArticleSize[],
            price: articlePrice,
            formattedPrice: getFormattedPrice(articlePrice, language),
            prepacks: _.map(prepacks, (prepack) => ({
                ...prepack,
                stock: {
                    available: _.get(prepack, 'stock.available', 0),
                    backorder: _.get(prepack, 'stock.backorder'),
                    total: _.get(prepack, 'stock.available', 0) + _.get(prepack.stock, 'backorder.available', 0),
                    selectedAmount: 0
                }
            })),
            certificates: _.get(extractCertificates, 'manufacturerAttributes.certificates', null)
        };

        const singleSizesHaveStock = _.some(
            article.sizes,
            (size) => size.stock.available > 0 || _.get(size, 'stock.backorder.available', 0) > 0
        );
        const prepacksHaveStock = _.some(article.prepacks, (prepack) => prepack.stock.available > 0);
        const hasStock = singleSizesHaveStock || prepacksHaveStock;

        return { ...article, hasStock };
    }

    private mapProductToReorderArticleSize(product: ProductForOrder, language: string): ReorderArticleSize {
        return {
            gtin: product.gtin,
            name: product.name,
            size: _.toUpper(product.size),
            brandName: product.brandName,
            articleNumber: product.articleNumber,
            showsDefaultPricelist: product.showsDefaultPricelist,
            images: product.images,
            stock: {
                available: product.stock.available,
                backorder: product.stock.backorder,
                total: product.stock.available + _.get(product.stock, 'backorder.available', 0),
                selectedAmount: 0,
                storeStock: product.stock.storeStock
            },
            manufacturerAttributes: product.manufacturerAttributes,
            fcAttributes: product.fcAttributes,
            price: product.price,
            formattedPrice: getFormattedPrice(product.price, language),
            ...(product.detailedSize ? { detailedSize: product.detailedSize } : {}),
            ...(product.orderIncrement ? { orderIncrement: product.orderIncrement } : {})
        };
    }

    private mapProductsToPreorderArticle({
        products,
        color,
        brandId,
        styleGroupId,
        language,
        colorMap
    }: {
        products: ProductForOrder[];
        color: OrderArticleColor;
        brandId: string;
        styleGroupId: string;
        language: string;
        colorMap: {
            products: string[];
            prepacks: PrepackForOrder[];
        };
    }): PreorderArticle {
        const sizes: PreorderArticleSize[] = _.map(products, (product: ProductForOrder) =>
            this.mapProductToPreorderArticleSize(product, language)
        );
        const articlePrice = getArticlePrice(products);
        const firstSize = _.first(sizes);
        const deliveryDates = (firstSize.manufacturerAttributes.deliveryDates as (string | DeliveryDateRange)[]).map(
            formatDeliveryDateToItem
        ) as Item<string>[] | Item<DeliveryDateRange>[];

        const article = {
            brandId,
            brandName: firstSize.brandName,
            styleGroupId,
            articleNumber: firstSize.articleNumber,
            name: firstSize.name,
            images: firstSize.images,
            imageUrl: getArticleMainImageUrl(
                _.map(products, (product) => ({ ...product, media: { images: product.images } }))
            ),
            isPreliminary: _.some(firstSize.images, { isPreliminary: true }),
            validAmount: true,
            hasDiscountedPurchasePrice: !!articlePrice.discountedPurchasePrice,
            showsDefaultPricelist: firstSize.showsDefaultPricelist,
            manufacturerAttributes: firstSize.manufacturerAttributes,
            fcAttributes: firstSize.fcAttributes,
            color,
            formattedColor: getFormattedColor(color),
            sizes: sortSizes(sizes) as PreorderArticleSize[],
            price: articlePrice,
            formattedPrice: getFormattedPrice(articlePrice, language),
            selectedDeliveryDate: deliveryDates[0],
            deliveryDates,
            isOrderableAsSingleSize: !!colorMap.products.length,
            prepacks: _.map(colorMap.prepacks, (prepack) => ({
                ...prepack,
                stock: {
                    available: _.get(prepack, 'stock.available', 0),
                    selectedAmount: 0
                }
            }))
        };

        return article;
    }

    private mapProductToPreorderArticleSize(product: ProductForOrder, language: string): PreorderArticleSize {
        return {
            gtin: product.gtin,
            name: product.name,
            size: _.toUpper(product.size),
            brandName: product.brandName,
            articleNumber: product.articleNumber,
            showsDefaultPricelist: product.showsDefaultPricelist,
            images: product.images,
            stock: { selectedAmount: 0 },
            manufacturerAttributes: product.manufacturerAttributes,
            fcAttributes: product.fcAttributes,
            price: product.price,
            formattedPrice: getFormattedPrice(product.price, language),
            ...(product.detailedSize ? { detailedSize: product.detailedSize } : {})
        };
    }

    private getColorsFromProducts(productsForOrder: ProductForOrder[]): OrderArticleColor[] {
        const getProductColors = (products) =>
            _.map(products, (product) => ({
                code: _.get(product, 'manufacturerAttributes.colorCode'),
                colorGroupId: _.get(product, 'colorGroupId'),
                name: _.get(product, 'color'),
                key: _.get(product, 'colorKey')
            }));
        const getUniqueColors = (colors) => _.uniqBy(colors, 'colorGroupId');
        const sortColorsByCode = (colors) => _.sortBy(colors, ({ code }) => (code !== '' ? code : undefined));

        return sortColorsByCode(getUniqueColors(getProductColors(productsForOrder)));
    }

    private getArticleMinimumOrderRestriction(
        products: ProductForOrder[],
        language: string
    ): MinimumOrderRestriction & {
        value: string;
    } {
        const minimumOrderRestriction: MinimumOrderRestriction = _.get(products, '[0].minimumOrderRestriction');
        const currency: string = _.toUpper(_.get(products, '[0].price.currency'));
        const type = _.get(minimumOrderRestriction, 'type');

        if (!type) {
            return null;
        }

        const parameter = minimumOrderRestriction.parameter;
        return {
            type,
            parameter,
            value:
                type === OrderRestrictionType.OrderVolume
                    ? !parameter[currency]
                        ? null
                        : formatPrice(parameter[currency], currency, language)
                    : _.toString(parameter)
        };
    }

    private getArticleAvailableStock(products: ProductForOrder[]): number {
        return _.reduce(products, (totalStock, { stock }: ProductForOrder) => totalStock + stock.available, 0);
    }

    private sortArticlesByStock(articles: ReorderArticle[]): ReorderArticle[] {
        return _.orderBy(articles, 'stock.available', 'desc');
    }

    private sortColorsByHasStock(colors: OrderArticleColor[]): OrderArticleColor[] {
        return _.orderBy(colors, 'hasStock', 'desc');
    }
}
